import React from 'react';

export interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  quality?: number;
  format?: 'webp' | 'avif' | 'jpg' | 'png';
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  className?: string;
}

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  quality = 80,
  format = 'webp',
  loading = 'lazy',
  fetchPriority = 'auto',
  className,
  style,
  ...props
}) => {
  let normalizedSrc = src;

  // Replace .jpg/.jpeg/.png with .webp if format is webp
  if (
    format === 'webp' &&
    typeof normalizedSrc === 'string' &&
    /\.(jpg|jpeg|png)$/i.test(normalizedSrc)
  ) {
    normalizedSrc = normalizedSrc.replace(/\.(jpg|jpeg|png)$/i, '.webp');
  }

  // Construct Netlify Image CDN URL when path starts with / or is relative
  const isNetlifyCdnEligible = typeof normalizedSrc === 'string' && (normalizedSrc.startsWith('/') || normalizedSrc.startsWith(process.env.PUBLIC_URL || ''));

  let finalSrc = normalizedSrc;
  if (isNetlifyCdnEligible) {
    const cleanUrl = normalizedSrc.replace(process.env.PUBLIC_URL || '', '');
    const params = new URLSearchParams();
    params.set('url', cleanUrl.startsWith('/') ? cleanUrl : `/${cleanUrl}`);
    if (width) params.set('w', String(width));
    if (quality) params.set('q', String(quality));
    if (format) params.set('fm', format);

    const isProduction = process.env.NODE_ENV === 'production';
    if (isProduction) {
      finalSrc = `/.netlify/images?${params.toString()}`;
    }
  }

  return (
    <img
      src={finalSrc}
      alt={alt}
      width={width}
      height={height}
      loading={loading}
      // @ts-ignore HTML fetchpriority attribute support
      fetchpriority={fetchPriority}
      className={className}
      style={style}
      {...props}
    />
  );
};

export default OptimizedImage;
